/*
 * The homepage membership portal.
 *
 * Liquid renders the standing prices, which are always correct. This adds the
 * introductory offer on top: what the first year costs, and the percentage
 * that saves against paying monthly. Both come from
 * assets/od-membership-sale.js, which holds the formulas the plans page uses,
 * so the two pages cannot quote different discounts.
 *
 * Currency is not computed here. ODMoney learns the visitor's format from a
 * price Liquid already rendered on the card, so a yen or euro member sees the
 * sale price written the way Shopify writes every other price on the store.
 *
 * Watching the document rather than listening for a route. The theme
 * dispatches pushstate when a link is pressed, before the new page has been
 * fetched, so a timer started there expires while the old markup is still on
 * screen and nothing runs again once the new section lands. An observer has no
 * window to get wrong. This is what od-membership-plans.js does, and why a
 * product page keeps its sale price across a transition.
 */
(function () {
  "use strict";

  var BOUND = 'odPortalBound';
  var pending = null;

  function salePercentage() {
    /* One request per page load however many sections ask, and never a second
       one on a transition: the answer cannot change between two clicks. */
    if (!pending) {
      pending = window.ODMembershipSale
        ? window.ODMembershipSale.fetchPercentage()
        : Promise.resolve(0);
    }
    return pending;
  }

  function moneyFor(el) {
    /* The figure on the card, in the visitor's own format. If ODMoney cannot
       read it back it returns null and we leave the price exactly as Liquid
       wrote it -- an unstyled number is worse than no discount shown. */
    if (!window.ODMoney || !el) return null;

    var cents = el.getAttribute('data-od-cents');
    return window.ODMoney.learn(cents, el.textContent);
  }

  function applyTier(tier, percentage, sale) {
    var annual = Number(tier.getAttribute('data-annual-cents'));
    var amount = tier.querySelector('[data-od-portal-amount]');
    var secondary = tier.querySelector('[data-od-portal-secondary]');
    if (!amount || !secondary || !isFinite(annual) || annual <= 0) return;

    var first = sale.introCents(annual, percentage);
    if (first === null) return;

    /* Learned from the per-month figure already on the card, because that is
       the number about to be replaced. */
    amount.setAttribute('data-od-cents', String(Math.round(annual / 12)));
    var money = moneyFor(amount);
    if (!money) return;

    var standingPerMonth = amount.textContent;

    amount.textContent = money(Math.round(first / 12));

    /* The standing price stays on the card, struck through. Removing it would
       leave a cheap number with nothing to be cheap against. */
    if (!tier.querySelector('.mp__was')) {
      var was = document.createElement('span');
      was.className = 'mp__was';
      was.textContent = standingPerMonth;
      amount.parentNode.insertBefore(was, amount);
    }

    /* Both totals, in the plans page's words. The discount covers the first
       year only and the second year must never be a surprise. */
    secondary.textContent =
      money(first) + ' ' + (tier.getAttribute('data-first-year') || 'for your first year') +
      ', ' + (tier.getAttribute('data-then') || 'then') + ' ' + money(annual) + ' a year';
  }

  function applyOffer(section, percentage, sale) {
    var offer = section.querySelector('[data-od-portal-offer]');
    var target = section.querySelector('[data-od-portal-percent]');
    var tier = section.querySelector('[data-od-portal-tier]');
    if (!offer || !target || !tier) return;

    var percent = sale.yearlySavingPercent(
      tier.getAttribute('data-annual-cents'),
      tier.getAttribute('data-monthly-year-cents'),
      percentage
    );

    /* Nothing rather than "Save 0%". A band that announces no saving is worse
       than a band that is not there. */
    if (percent === null) return;

    target.textContent = percent + '%';
    offer.hidden = false;
  }

  /* The one price on the phone card. It says "from", so it takes the
     cheapest plan, and while a sale runs that is the sale price. */
  function applyMobileFrom(section, percentage, sale) {
    var from = section.querySelector('[data-od-portal-from]');
    if (!from) return;

    var annual = Number(from.getAttribute('data-annual-cents'));
    var first = sale.introCents(annual, percentage);
    if (first === null) return;

    from.setAttribute('data-od-cents', String(Math.round(annual / 12)));
    var money = moneyFor(from);
    if (!money) return;

    from.textContent = money(Math.round(first / 12));
  }

  function init(section) {
    if (!section || section.dataset[BOUND] === '1') return;
    section.dataset[BOUND] = '1';

    var sale = window.ODMembershipSale;
    if (!sale) return;

    salePercentage().then(function (percentage) {
      if (!percentage) return;
      if (!section.isConnected) return;

      var tiers = section.querySelectorAll('[data-od-portal-tier]');
      for (var i = 0; i < tiers.length; i++) applyTier(tiers[i], percentage, sale);

      applyOffer(section, percentage, sale);
      applyMobileFrom(section, percentage, sale);
    });
  }

  function scan() {
    var sections = document.querySelectorAll('[data-od-portal]');
    for (var i = 0; i < sections.length; i++) init(sections[i]);
  }

  scan();

  if (typeof MutationObserver === 'function') {
    /* Cheap: it only looks for the section, and does nothing once bound. The
       observer is never disconnected because the homepage can be returned to
       any number of times, and a fresh section arrives each time. */
    new MutationObserver(scan).observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  document.addEventListener('od:main-content-replaced', scan);
  document.addEventListener('od:after-swap', scan);
})();
